/** Thin fetch wrapper. The session is an httpOnly cookie, so nothing is stored here. */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    // Spread rather than assigned: under exactOptionalPropertyTypes, handing
    // `headers` an explicit undefined is not the same as omitting it.
    ...(init?.body ? { headers: { 'Content-Type': 'application/json' } } : {}),
    ...init,
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `خطای ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export class UnauthorizedError extends Error {
  constructor() {
    super('unauthorized');
  }
}

export type User = {
  sub?: string;
  id?: string;
  email: string;
  displayName?: string;
  role: 'admin' | 'operator';
};

export type Inbox = {
  id: string;
  slug: string;
  name: string;
  siteUrl: string | null;
  wooBaseUrl: string | null;
  wooKeyHint: string | null;
  hasCredentials: boolean;
  isActive: boolean;
  widgetConfig: {
    bubblePosition?: string;
    brandColor?: string;
    handoffMode?: 'chat' | 'callback';
  };
  /** Null until the catalog has been pulled once. */
  lastSyncedAt: string | null;

};

export type Product = {
  id: string;
  wooId: number;
  sku: string | null;
  name: string;
  brand: string | null;
  price: string | null;
  stockStatus: 'instock' | 'outofstock' | 'onbackorder';
  stockQuantity: number | null;
  permalink: string | null;
  imageUrl: string | null;
  syncedAt: string;
  /** The INCI list as the shop typed it, for when nothing matched. */
  ingredientsRaw: string | null;
  category: string | null;
  /** What the product is, read from its own text. Shown, never edited here. */
  traits: string[] | null;
  concerns: string[] | null;
  spf: number | null;
  routineSlot: string | null;
  pregnancyStatus: 'safe' | 'unsafe' | 'unknown' | null;
  reviewStatus: string | null;
  /** The shop's own 1..5 rating; null means unrated. 3 is neutral. */
  editorialRank: number | null;
  source: string | null;
  ingredients: string[];
};

export type Stats = {
  totalProducts: number;
  brands: number;
  byPregnancy: { status: string; n: number }[];
  byCategory: { category: string; n: number }[];
  uncategorized: number;
  byReview: { status: string; n: number }[];
};

/**
 * What the assistant did today. "Today" is a Tehran day, decided by the server.
 *
 * Everything here except `operator.waitingNow` and `operator.onlineNow` is a
 * figure for the day; those two are the present moment, which is why they are
 * the ones that can send somebody running.
 */
export type Dashboard = {
  inbox: {
    id: string;
    name: string;
    isActive: boolean;
    lastSyncedAt: string | null;
    lastSyncReport: Record<string, number> | null;
  } | null;
  conversations: { today: number; yesterday: number };
  operator: { handoffsToday: number; waitingNow: number; onlineNow: number };
  assistant: {
    replies: number;
    productsOffered: number;
    uniqueProducts: number;
    admittedDefeat: number;
    rewrittenClaims: number;
    /** Null when no reply today carried a timing — render "—", never zero. */
    avgMs: number | null;
    slowestMs: number | null;
    timedReplies: number;
  };
  usage: { promptTokens: number; completionTokens: number };
  gates: { gate: string; n: number }[];
  byHour: { hour: number; n: number }[];
  /** Grouped by identical wording only — two phrasings stay two rows. */
  shortfalls: { text: string; gate: string | null; n: number }[];
  catalog: { products: number; pending: number; uncategorized: number; ranked: number };
};

/** One person waiting for a call back, as the list needs them. */
export type CallbackRow = {
  id: string;
  sessionId: string;
  reason: string;
  status: string;
  channel: 'whatsapp' | 'telegram' | 'sms' | 'call';
  contactValue: string | null;
  requestedAt: string;
  resolvedAt: string | null;
  displayName: string | null;
  visitorNumber: number | null;
  identityVerified: boolean;
  problem: string | null;
};

export const api = {
  login: (email: string, password: string) =>
    request<{ user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => request<{ user: User }>('/auth/me'),
  stats: (inboxId?: string) =>
    request<Stats>(`/stats${inboxId ? `?inboxId=${inboxId}` : ''}`),
  dashboard: (inboxId?: string) =>
    request<Dashboard>(`/dashboard${inboxId ? `?inboxId=${inboxId}` : ''}`),
  inboxes: () => request<{ inboxes: Inbox[] }>('/inboxes'),
  createInbox: (payload: Record<string, unknown>) =>
    request<{ inbox: Inbox }>('/inboxes', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  syncCatalog: (id: string) =>
    request<{ report: Record<string, number>; syncedAt: string }>(`/inboxes/${id}/sync`, {
      method: 'POST',
    }),
  updateInbox: (id: string, payload: Record<string, unknown>) =>
    request<{ inbox: Inbox }>(`/inboxes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  products: (params: Record<string, string>) =>
    request<{ products: Product[]; page: number; pageSize: number; total: number }>(
      `/products?${new URLSearchParams(params)}`,
    ),
  review: (productIds: string[], patch: Record<string, unknown>) =>
    request<{ updated: number }>('/products/review', {
      method: 'POST',
      body: JSON.stringify({ productIds, ...patch }),
    }),

  callbacks: (params: Record<string, string>) =>
    request<{ callbacks: CallbackRow[] }>(`/callbacks?${new URLSearchParams(params)}`),
  callbackDone: (id: string) =>
    request<{ ok: true }>(`/callbacks/${id}/done`, { method: 'POST' }),
  conversations: (params: Record<string, string>) =>
    request<{
      conversations: ConversationRow[];
      total: number;
      queue: { status: string; n: number }[];
      closedToday: number;
    }>(`/conversations?${new URLSearchParams(params)}`),
  conversation: (id: string) => request<ConversationDetail>(`/conversations/${id}`),
  replyToConversation: (id: string, text: string) =>
    request<unknown>(`/conversations/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  setConversationStatus: (id: string, status: string) =>
    request<unknown>(`/conversations/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  visitors: (params: Record<string, string>) =>
    request<{ visitors: Visitor[]; onlineCount: number; windowSeconds: number }>(
      `/visitors?${new URLSearchParams(params)}`,
    ),
  visitor: (id: string) => request<VisitorDetail>(`/visitors/${id}`),
  messageVisitor: (id: string, text: string) =>
    request<{ sessionId: string }>(`/visitors/${id}/message`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
};

export type Visitor = {
  id: string;
  visitorNumber: number;
  displayName: string | null;
  email: string | null;
  wpUserId: number | null;
  identityVerified: boolean;
  currentUrl: string | null;
  currentTitle: string | null;
  lastSeenAt: string;
  sessionId: string | null;
  sessionStatus: string | null;
  messageCount: number;
};

export type VisitorDetail = {
  visitor: Visitor & { createdAt: string };
  sessions: { id: string; status: string; lastMessageAt: string }[];
  pages: { url: string; title: string | null; viewedAt: string; productName: string | null }[];
};

export type ConversationRow = {
  id: string;
  status: string;
  startedAt: string;
  lastMessageAt: string;
  summary: string | null;
  customerId: string;
  anonId: string;
  wpUserId: number | null;
  displayName: string | null;
  visitorNumber: number;
  identityVerified: boolean;
  lastSeenAt: string;
  messageCount: number;
  lastMessage: string | null;
  /** Why it left the assistant. The row's headline tag. */
  handoffReason: string | null;
  /** Set while a handoff is still queued, so the timer is the wait not the age. */
  waitingSince: string | null;
  admittedDefeat: boolean;
};

export type ConversationDetail = {
  conversation: { id: string; status: string; startedAt: string };
  customer: {
    id: string;
    anonId: string;
    wpUserId: number | null;
    visitorNumber: number;
    displayName: string | null;
    email: string | null;
    phone: string | null;
    /** Whether WordPress signed the identity, or the visitor merely claimed it. */
    identityVerified: boolean;
    createdAt: string;
    lastSeenAt: string;
  };
  /** Only populated for a verified identity — see the route for why. */
  orders: {
    id: number;
    number: string;
    status: string;
    statusFa: string;
    total: string;
    items: { name: string; quantity: number }[];
  }[];
  skinProfile: {
    skinType: string | null;
    sensitivity: string | null;
    concerns: string[] | null;
    isPregnantOrNursing: boolean | null;
  } | null;
  messages: {
    id: string;
    role: 'user' | 'assistant' | 'operator' | 'system';
    content: string;
    meta: Record<string, unknown> | null;
    createdAt: string;
  }[];
  pages: {
    url: string;
    title: string | null;
    viewedAt: string;
    productName: string | null;
  }[];
  currentPage: {
    url: string;
    title: string | null;
    productName: string | null;
  } | null;
  handoffs: { id: string; reason: string; status: string }[];
};
